import React, {
	ColumnDef,
	useReactTable,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	SortingState,
	getSortedRowModel,
} from '@tanstack/react-table'
import { RefObject, useMemo, useState } from 'react'
import {
	AdminPanelSettings,
	Delete,
	InfoOutlined,
	KeyboardArrowDown,
	KeyboardArrowUp,
	Mail,
	UnfoldMore,
} from '@mui/icons-material'
import { Button, IconButton, Switch, Tooltip } from '../../components/ui'
import { useUserDateFormat } from '../useUserDateFormat'
import cn from '../../lib/utils/cn'
import { shortenFileName } from '../../lib/utils/formatDocument'
import { UserActionType } from '../../pages/Admin/UsersPage'
import { OrganizationUsersItem } from '../../lib/api/types/organization.interfaces'
import { isInviteExpired } from '../../lib/utils/isInviteExpired'

export type AdminUsersDataTableEntry = OrganizationUsersItem & {
	options: {
		isOwner: boolean
		isAccepted: boolean
		isExpired: boolean
		isDisabledForActiveSwitch: boolean
		isDisabledForAdminSwitch: boolean
		activeSwitchTooltip: string
		adminSwitchTooltip: string
		isAdminUser: boolean
		isDeactivated: boolean
	}
}

interface useCreateAdminUsersDataTableProps {
	data?: OrganizationUsersItem[] | null
	containerRef?: RefObject<HTMLDivElement>
	searchQuery?: string
	limit?: number
	offset?: number
	isMobile?: boolean
	isTablet?: boolean
	currentUserId?: string
	onActionClick?: (id: string, action: UserActionType) => void
	onToggleUserStatus: (userId: string, currentStatus: boolean) => void
	onToggleAdminRights: (user: OrganizationUsersItem) => void
	onReinviteUser: (user: { id: string; email: string }) => void
	onDeleteUser: (user: OrganizationUsersItem) => void
	onOpenUserInfo: (user: OrganizationUsersItem) => void
}

export const useCreateAdminUsersDataTable = ({
	data = [],
	searchQuery = '',
	limit = 10,
	offset = 0,
	isMobile,
	isTablet,
	currentUserId,
	onToggleUserStatus,
	onToggleAdminRights,
	onReinviteUser,
	onDeleteUser,
	onOpenUserInfo,
}: useCreateAdminUsersDataTableProps) => {
	const { formatDate, formatDateTime } = useUserDateFormat()
	const [sorting, setSorting] = useState<SortingState>([])

	const renderHeaderItem = (
		title: string,
		column: React.Column<AdminUsersDataTableEntry, unknown>,
		className?: string
	) => {
		return (
			<div
				className={cn(
					'w-full flex group justify-start items-center gap-1 cursor-pointer select-none h-5 group pl-4 dark:text-text-table-dark',
					className
				)}
			>
				<span className="text-center w-fit">{title}</span>
				<span
					className={cn(
						'flex items-center justify-center transition-all duration-200 w-3 h-5',
						column.getIsSorted() === false && 'hidden group-hover:inline-block'
					)}
				>
					{column.getIsSorted() === 'asc' && (
						<KeyboardArrowUp className="w-5 h-5" />
					)}
					{column.getIsSorted() === 'desc' && (
						<KeyboardArrowDown className="w-5 h-5" />
					)}
					{column.getIsSorted() === false && (
						<UnfoldMore className="h-5 w-5 opacity-0 group-hover:opacity-100 duration-default -ml-1" />
					)}
				</span>
			</div>
		)
	}

	const tableData: AdminUsersDataTableEntry[] = useMemo(() => {
		return (data ?? []).map((user) => {
			const isOwner = Boolean(user?.invite === null)

			const isAccepted = Boolean(
				!user.invite || (!!user.invite && user.invite?.acceptedAt)
			)
			const isExpired = isInviteExpired(user.invite)

			const isDeactivated = user?.isDeactivated

			const isDisabledForActiveSwitch =
				(user.invite && !user.invite?.acceptedAt) ||
				currentUserId === user.id ||
				isOwner

			const isDisabledForAdminSwitch =
				isDisabledForActiveSwitch || isDeactivated

			const activeSwitchTooltip = isOwner
				? 'You cannot change it for the owner of the organization.'
				: user?.invite && !user.invite?.acceptedAt
				? "User hasn't accepted invite yet"
				: currentUserId === user.id
				? 'You cannot change this for your account.'
				: 'Only organization owner can do it'

			const adminSwitchTooltip = isOwner
				? 'You cannot change it for the owner of the organization.'
				: user?.invite && !user.invite?.acceptedAt
				? "User hasn't accepted invite yet"
				: currentUserId === user.id
				? 'You cannot change this for your account.'
				: isDeactivated
				? "User isn't activated"
				: 'Only organization owner can do it'

			const isAdminUser = user.roles?.some((r: any) => r?.isAdministrator)

			return {
				...user,
				options: {
					isOwner,
					isAccepted,
					isExpired,
					isDisabledForActiveSwitch,
					isDisabledForAdminSwitch,
					activeSwitchTooltip,
					adminSwitchTooltip,
					isAdminUser,
					isDeactivated,
				},
			}
		})
	}, [data, currentUserId])

	const columns = useMemo<ColumnDef<AdminUsersDataTableEntry>[]>(() => {
		const base: ColumnDef<AdminUsersDataTableEntry>[] = [
			{
				accessorKey: 'name',
				enableResizing: !isMobile,
				size: isMobile ? 150 : 100,
				minSize: 50,
				maxSize: 160,
				header: ({ column }) => renderHeaderItem('Name', column),
				cell: (info) => {
					const value = String(info.getValue() ?? '')
					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis dark:text-text-table-dark">
							<Tooltip
								label={value}
								className={cn(
									'max-w-[100px] text-wrap',
									isMobile && 'opacity-0'
								)}
							>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile ? shortenFileName(value) : value}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'email',
				enableResizing: !isMobile,
				size: 100,
				minSize: 50,
				maxSize: 180,
				header: ({ column }) => renderHeaderItem('Email', column),
				cell: (info) => {
					const value = String(info.getValue() ?? '')
					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis dark:text-text-table-dark">
							<Tooltip
								label={value}
								className={cn(
									'w-fit',
									isMobile && 'opacity-0 w-fit h-auto text-wrap'
								)}
							>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile ? shortenFileName(value) : value}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'modifiedAt',
				enableResizing: !isMobile,
				enableSorting: true,
				minSize: 50,
				size: 90,
				maxSize: 140,
				header: ({ column }) => renderHeaderItem('Modified Date', column),
				cell: ({ row }) => {
					const modifiedAt = row.original.modifiedAt
					return (
						<Tooltip label={modifiedAt ? formatDateTime(String(modifiedAt)) : ''} classNameChildrenWrapper="w-fit">
							<p className="text-start text-text-table dark:text-text-table-dark">
								{modifiedAt ? formatDate(String(modifiedAt)) : '-'}
							</p>
						</Tooltip>
					)
				},
			},
			{
				accessorKey: 'options',
				enableResizing: false,
				enableSorting: false,
				size: isMobile ? 120 : isTablet ? 150 : 180,
				maxSize: isMobile ? 120 : isTablet ? 150 : 180,
				minSize: isMobile ? 120 : isTablet ? 150 : 180,
				header: () => (
					<div className="flex items-center justify-end w-full">
						<span className="text-xs pr-8 sm:pr-12">Active</span>
						<span className="text-xs pr-8 sm:pr-12">Admin</span>
						<span className="text-xs px-2 sm:px-4"></span>
					</div>
				),
				cell: ({ row }) => {
					const { options } = row.original
					const user = row.original as any
					const {
						isAccepted,
						isExpired,
						isDisabledForActiveSwitch,
						isDisabledForAdminSwitch,
						activeSwitchTooltip,
						adminSwitchTooltip,
						isAdminUser,
						isDeactivated,
					} = options

					return (
						<div className="flex items-center justify-end w-auto gap-1 sm:gap-5 pr-2">
							{/* Active Switch */}
							<div className="flex items-center justify-center px-2">
								{!isAccepted && (
									<div
										className={cn(
											'mr-2 flex items-center justify-center text-text-secondary bg-transparent lg:bg-stroke-light rounded py-1 pl-2 h-9 box-border gap-3 md:flex w-24 lg:w-fit dark:text-text-main-dark dark:bg-background-light-dark',
											isExpired ? 'pr-1' : 'pr-2'
										)}
									>
										<span className="hidden lg:block text-xs">
											{isExpired ? 'Expired' : 'Invited'}
										</span>
										{isExpired && (
											<Button
												label={'Invite'}
												leftIcon={<Mail />}
												classNameContent={cn(
													'flex items-center justify-center gap-0'
												)}
												className={cn('w-full h-6 px-1 py-1 rounded text-xs')}
												onClick={() =>
													user.email &&
													onReinviteUser({ id: user.id, email: user.email })
												}
											/>
										)}
									</div>
								)}
								{!Boolean(!isAccepted && isMobile) && (
									<Switch
										enabled={!isDeactivated}
										wrapperClassName={cn(
											currentUserId === user.id && 'opacity-70'
										)}
										activeClassName=""
										inactiveClassName="bg-negative dark:bg-negative"
										onClick={() => onToggleUserStatus(user.id, isDeactivated)}
										disabled={isDisabledForActiveSwitch}
										tooltip={
											isDisabledForActiveSwitch ? activeSwitchTooltip : null
										}
									/>
								)}
							</div>

							{/* Admin Switch */}
							{!Boolean(!isAccepted && isMobile) && (
								<div className="flex items-center justify-center px-2">
									<Switch
										enabled={isAdminUser}
										wrapperClassName="flex"
										activeWrapperClassName="bg-accent disabled:bg-background-dark"
										inactiveWrapperClassName="border-stroke-light-hover"
										activeClassName={cn(
											'bg-white',
											isDisabledForAdminSwitch && 'bg-stroke-dark'
										)}
										inactiveClassName={cn(
											isDisabledForAdminSwitch
												? 'bg-background-light'
												: 'bg-sidebar-button-background dark:bg-white'
										)}
										activeIcon={
											<AdminPanelSettings
												className={cn(
													'w-2.5 h-2.5 fill-accent',
													isDisabledForAdminSwitch && 'fill-white'
												)}
											/>
										}
										inactiveIcon={
											<AdminPanelSettings
												className={cn(
													'w-2.5 h-2.5',
													isDisabledForAdminSwitch
														? 'fill-white'
														: 'fill-white dark:fill-black'
												)}
											/>
										}
										onClick={() => onToggleAdminRights(user)}
										disabled={isDisabledForAdminSwitch}
										tooltip={
											isDisabledForAdminSwitch ? adminSwitchTooltip : null
										}
									/>
								</div>
							)}

							{/* Actions Button */}
							<div className="flex items-center justify-center">
								<IconButton
									icon={
										isExpired ? (
											<Delete className="fill-text-main dark:fill-text-main-dark" />
										) : (
											<InfoOutlined className="fill-text-main dark:fill-text-main-dark" />
										)
									}
									className="w-8 h-8"
									variant="secondary"
									onClick={() =>
										isExpired ? onDeleteUser(user) : onOpenUserInfo(user)
									}
									tooltip={isExpired ? 'Delete user' : 'View user'}
								/>
							</div>
						</div>
					)
				},
			},
		]
		if (isMobile) {
			return base.filter((columnDef) => {
				const accessorKey = (columnDef as any)
					?.accessorKey as keyof AdminUsersDataTableEntry
				return accessorKey && ['id', 'name', 'options'].includes(accessorKey)
			})
		}

		if (isTablet) {
			return base.filter((columnDef) => {
				const accessorKey = (columnDef as any)
					?.accessorKey as keyof AdminUsersDataTableEntry
				return (
					accessorKey &&
					['id', 'name', 'email', 'options'].includes(accessorKey)
				)
			})
		}

		return base
	}, [isMobile, isTablet, tableData])

	const table = useReactTable({
		data: tableData,
		columns,
		columnResizeMode: 'onChange',
		enableColumnResizing: true,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getSortedRowModel: getSortedRowModel(),
		manualSorting: false,
		enableSorting: true,
		enableSortingRemoval: true,
		onSortingChange: setSorting,
		state: {
			sorting,
			globalFilter: searchQuery,
			pagination: {
				pageSize: limit,
				pageIndex: offset / limit,
			},
		},
		globalFilterFn: (row, columnId, filterValue) => {
			const searchValue = String(filterValue).toLowerCase()
			const name = String(row.original.name ?? '').toLowerCase()
			const email = String(row.original.email ?? '').toLowerCase()
			return name.includes(searchValue) || email.includes(searchValue)
		},
	})

	return { table }
}
